import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import styled from '@emotion/styled';
import { ViewerItem } from 'presentations/api/schema/user';
import Avatar from 'presentations/views/components/atoms/Avatar';
import Icon from 'presentations/views/components/atoms/Icon';
import Headline from 'presentations/views/components/atoms/Headline';
import IconButton from 'presentations/views/components/atoms/IconButton';
import MessageList from '../MessageList';
import ChatFormTemplate from '../ChatFormTemplate';
import {
  borderRadius,
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import {
  blueGrayPale,
  gray01,
  gray02,
  gray04,
  primary,
  white,
} from 'presentations/views/styles/color';
import { useHomeView } from './hooks';
import SubmitForm from '../SubmitForm';
import { ActiveIndex } from 'presentations/reducers/AssistantWidget';
import { SyntheticEvent } from 'react';
import { imagePath } from 'presentations/assets';
import { helpPath } from 'presentations/routes/path';

interface Props {
  viewer: ViewerItem;
  onClickBack: () => void;
  onClickPresetView: (activeIndex: number) => void;
  onSubmitMessage: (content: string) => void;
}

export default function Component(props: Props) {
  const { state, actions } = useHomeView({
    viewer: props.viewer,
    onSubmitMessage: props.onSubmitMessage,
  });
  const t = translateInComponent([translations]);

  const onClickPreset = (e: SyntheticEvent<HTMLDivElement>) => {
    const id = e.currentTarget.dataset.id;
    props.onClickPresetView(Number(id));
  };

  return (
    <ChatFormTemplate
      header={
        <Header>
          <HeaderLeftItem>
            <HeaderTitle>
              <TitleAvatar src={imagePath('assistant/icon.svg')} />
              <Title variant="h2">Jicoo AI</Title>
              <HelpIconButton href={helpPath('8439109')} target="_blank">
                <HelpIcon name="help" />
              </HelpIconButton>
              <BetaIcon>BETA</BetaIcon>
            </HeaderTitle>
          </HeaderLeftItem>
          <HeaderRightItem>
            <IconButton onClick={props.onClickBack}>
              <RightIcon name="close" />
            </IconButton>
          </HeaderRightItem>
        </Header>
      }
      content={
        <Main>
          <MenuList>
            <MenuButton
              data-id={ActiveIndex.ScheduleChat}
              onClick={onClickPreset}
            >
              <MenuButtonContent>
                <MenuButtonTitle>{t('titles.0')}</MenuButtonTitle>
                <MenuItemDescription>{t('descriptions.0')}</MenuItemDescription>
              </MenuButtonContent>
              <MenuButtonIcon name="chevron-right" />
            </MenuButton>
            <MenuButton
              data-id={ActiveIndex.OptimizeChat}
              onClick={onClickPreset}
            >
              <MenuButtonContent>
                <MenuButtonTitle>{t('titles.1')}</MenuButtonTitle>
                <MenuItemDescription>{t('descriptions.1')}</MenuItemDescription>
              </MenuButtonContent>
              <MenuButtonIcon name="chevron-right" />
            </MenuButton>
            <MenuButton
              data-id={ActiveIndex.SummaryChat}
              onClick={onClickPreset}
            >
              <MenuButtonContent>
                <MenuButtonTitle>{t('titles.2')}</MenuButtonTitle>
                <MenuItemDescription>{t('descriptions.2')}</MenuItemDescription>
              </MenuButtonContent>
              <MenuButtonIcon name="chevron-right" />
            </MenuButton>
          </MenuList>
          <MessageList
            viewer={props.viewer}
            isThinking={false}
            messageStream={null}
            messages={state.messages}
          />
        </Main>
      }
      footer={
        <Footer>
          <SubmitForm
            isLoading={false}
            onChange={actions.onChangeMessage}
            onSubmit={actions.onSubmitMessage}
            content={state.content}
          />
        </Footer>
      }
    />
  );
}
const Header = styled.div`
  padding: 0 ${spacing(12)};
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${gray04};
`;
const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
`;
const HeaderLeftItem = styled.div`
  display: flex;
  justify-content: flex-start;
`;
const HeaderRightItem = styled.div`
  display: flex;
  justify-content: flex-end;
`;
const TitleAvatar = styled(Avatar)`
  width: 26px;
  height: 26px;
  margin-right: ${spacing(8)};
`;
const Footer = styled.div`
  border-top: 1px solid ${gray04};
`;
const Main = styled.div`
  display: flex;
  flex-direction: column-reverse;
  height: calc(100vh / 5 * 3);
  overflow-y: auto;
  padding: ${spacing(16)} 0;
  ${mediaMax.tablet} {
    height: initial;
    overflow-y: hidden;
  }
`;

const MenuList = styled.div`
  margin-top: ${spacing(24)};
  margin-bottom: ${spacing(16)};
  padding: 0 ${spacing(12)};
  height: fit-content;
`;
const MenuButton = styled.div`
  width: 100%;
  height: fit-content;
  cursor: pointer;
  color: ${primary};
  border-radius: ${borderRadius(6)};
  background-color: ${white};
  border: 1px solid ${gray04};
  margin-bottom: ${spacing(8)};
  display: flex;
  align-items: center;
  :hover {
    border-color: ${primary};
  }
`;
const MenuButtonContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: ${spacing(8)} ${spacing(16)};
  flex: 1;
`;
const MenuButtonIcon = styled(Icon)`
  width: 24px !important;
  height: 24px !important;
  margin-right: ${spacing(8)};
`;
const MenuButtonTitle = styled.div`
  font-weight: ${fontWeight('bold')};
`;

const MenuItemDescription = styled.div`
  color: ${gray01};
  margin-top: ${spacing(4)};
`;
const BetaIcon = styled.div`
  font-weight: ${fontWeight('normal')};
  margin-left: ${spacing(4)};
  font-size: ${fontSize(11)};
  line-height: ${fontSize(11)};
  border: 1px solid ${gray02};
  border-radius: ${borderRadius(4)};
  padding: ${spacing(6)};
  color: ${gray02};
  height: fit-content;
`;
const Title = styled(Headline)`
  display: flex;
  align-items: center;
`;

const RightIcon = styled(Icon)`
  color: ${gray01};
  width: 22px !important;
  height: 22px !important;
`;

const HelpIconButton = styled(IconButton)`
  margin-left: ${spacing(4)};
`;
const HelpIcon = styled(Icon)`
  width: 18px !important;
  height: 18px !important;
  color: ${gray02};
`;
